import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listQueuesLive, signOutSeat, type QueueAgentLive, type QueueLive } from "../api/queues";
import { QueueAssignmentMatrix } from "../components/QueueAssignmentMatrix";
import { TransfersLiveCard } from "../components/TransfersLiveCard";

// Staffing changes on the timescale of someone putting a headset on, so a
// 10s poll is honest without hammering the API. The timestamp in the header
// exists so a stalled tab is obvious rather than quietly showing old numbers.
const REFRESH_MS = 10_000;

const STATUS_TONE: Record<QueueAgentLive["status"], string> = {
  ready: "bg-green-100 text-green-800",
  "on a call": "bg-brand/10 text-brand-dark",
  "on break": "bg-amber-100 text-amber-800",
  // A fault, not a choice — the agent thinks they are signed in.
  unreachable: "bg-red-100 text-red-800",
  offline: "bg-gray-200 text-gray-600",
};

function StatusBadge({ status }: { status: QueueAgentLive["status"] }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${STATUS_TONE[status]}`}>
      {status}
    </span>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: string;
}) {
  return (
    <div className="text-center">
      <div className={`text-2xl font-semibold ${tone ?? "text-gray-900"}`}>
        {value}
      </div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

/** Collapse "lane" campaigns back into the one campaign they represent.
 *
 * A campaign is sometimes split across several runners because ONE runner is
 * paced by its inter-call jitter to about 5.7 calls/sec regardless of its
 * channel cap — a second lane roughly doubles throughput without leaning any
 * harder on the carrier per call. That is a dialer implementation detail, and
 * it has no business on a floor manager's screen: they supervise one campaign,
 * and splitting it across cards made them flip between pages to answer "is the
 * floor keeping up?".
 *
 * Lanes are matched on the naming convention (`Name — lane 2`), so an ordinary
 * campaign whose name merely contains the word is untouched. Counts sum;
 * abandon rate is recomputed from the summed totals rather than averaged,
 * because averaging two rates over different denominators is simply wrong.
 * Agents are unioned by extension, since the same rep answers every lane.
 */
const LANE_SUFFIX = /\s*[—-]\s*lane\s*\d+\s*$/i;

export function mergeLanes(queues: QueueLive[]): QueueLive[] {
  const groups = new Map<string, QueueLive[]>();
  for (const q of queues) {
    const base = q.campaign_name.replace(LANE_SUFFIX, "").trim();
    const list = groups.get(base);
    if (list) list.push(q);
    else groups.set(base, [q]);
  }

  return [...groups.entries()].map(([base, lanes]) => {
    if (lanes.length === 1) return lanes[0];
    // The lowest campaign_id is the original; keep it so links and queue
    // assignment still land somewhere real.
    const primary = [...lanes].sort((a, b) => a.campaign_id - b.campaign_id)[0];
    const sum = (f: (q: QueueLive) => number) =>
      lanes.reduce((n, q) => n + f(q), 0);

    const agents = new Map<number, QueueAgentLive>();
    for (const q of lanes) {
      for (const a of q.agents) {
        const existing = agents.get(a.extension_id);
        // One rep, one row: keep whichever lane saw them most recently busy.
        if (!existing || (a.calls_taken ?? 0) > (existing.calls_taken ?? 0)) {
          agents.set(a.extension_id, a);
        }
      }
    }

    const entered = sum((q) => q.entered);
    const abandoned = sum((q) => q.abandoned);
    const connected = sum((q) => q.connected);

    return {
      ...primary,
      campaign_name: base,
      queue_name: lanes.map((q) => q.queue_name).join(", "),
      members: agents.size,
      valid_members: sum((q) => q.valid_members),
      // Distinct reps, not a sum: the same person is a member of every lane,
      // so adding the per-lane counts would report the floor as twice its size.
      signed_in: new Set(
        lanes.flatMap((q) =>
          q.agents.filter((a) => a.status !== "offline").map((a) => a.extension_id),
        ),
      ).size,
      ready: sum((q) => q.ready),
      on_call: sum((q) => q.on_call),
      paused: sum((q) => q.paused),
      // Only true when NO lane is taking calls; one working lane means the
      // floor is covered.
      no_one_taking_calls: lanes.every((q) => q.no_one_taking_calls),
      entered,
      connected,
      abandoned,
      abandon_pct: entered > 0 ? Math.round((1000 * abandoned) / entered) / 10 : 0,
      avg_hold_s:
        entered > 0
          ? Math.round(
              (10 * sum((q) => q.avg_hold_s * q.entered)) / entered,
            ) / 10
          : 0,
      agents: [...agents.values()],
    };
  });
}

function QueueCard({ queue }: { queue: QueueLive }) {
  // "Taking calls" is ready + on-call: someone mid-conversation still counts,
  // they hang up and take the next one. Only zero of both is the alarm.
  const taking = queue.ready + queue.on_call;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="font-medium truncate">{queue.campaign_name}</div>
          <div className="text-xs text-gray-500 font-mono">
            {queue.queue_name}
          </div>
        </div>
        {queue.no_one_taking_calls ? (
          <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800 whitespace-nowrap">
            No one taking calls
          </span>
        ) : (
          <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800 whitespace-nowrap">
            {taking} taking calls
          </span>
        )}
      </div>

      <div className="grid grid-cols-5 gap-2 px-4 py-3 border-b border-gray-100">
        <Stat label="assigned" value={queue.members} />
        <Stat label="signed in" value={queue.signed_in} />
        <Stat
          label="ready"
          value={queue.ready}
          tone={queue.ready > 0 ? "text-green-700" : "text-gray-400"}
        />
        <Stat label="on a call" value={queue.on_call} />
        <Stat
          label="unavailable"
          value={queue.paused}
          tone={queue.paused > 0 ? "text-amber-700" : "text-gray-400"}
        />
      </div>

      {queue.entered > 0 && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 px-4 py-2 border-b border-gray-100 text-xs text-gray-600">
          <span>
            <span className="font-medium text-gray-900">{queue.connected}</span> connected
            {" of "}
            <span className="font-medium text-gray-900">{queue.entered}</span> transferred in
          </span>
          <span>
            {/* The number that says the dialer is outpacing the agents. It ran
                at 50% before pacing was fixed and nothing on screen showed it. */}
            <span className={queue.abandon_pct >= 20 ? "font-medium text-red-700" : "font-medium text-gray-900"}>
              {queue.abandon_pct}%
            </span>{" "}
            gave up waiting ({queue.abandoned})
          </span>
          <span>
            avg wait{" "}
            <span className={queue.avg_hold_s >= 30 ? "font-medium text-amber-700" : "font-medium text-gray-900"}>
              {queue.avg_hold_s}s
            </span>
          </span>
          <span className="text-gray-400">last 12h</span>
        </div>
      )}

      {queue.no_one_taking_calls && queue.members > 0 && (
        <div className="px-4 py-2 bg-red-50 text-red-800 text-xs">
          {queue.members} agent{queue.members === 1 ? "" : "s"} assigned to this
          queue, but none can take a transfer right now. Callers who press to
          connect will ring until the queue times out.
        </div>
      )}

      {queue.members === 0 ? (
        <div className="px-4 py-3 text-sm text-gray-500">
          No agents assigned to this queue.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Agent</th>
              <th className="px-4 py-2">Extension</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Calls</th>
              <th className="px-4 py-2">Time since last call</th>
              <th className="px-4 py-2 w-px"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {[...queue.agents].sort(byLongestWait).map((a) => (
              <tr key={a.extension_id}>
                <td className="px-4 py-2">
                  {a.extension_title || (
                    <span className="text-gray-400">(unnamed)</span>
                  )}
                </td>
                <td className="px-4 py-2 font-mono text-xs">
                  {a.extension_name}
                </td>
                <td className="px-4 py-2">
                  <StatusBadge status={a.status} />
                  {/* Surfaced separately from the badge: an assigned agent the
                      realtime view will not resolve is invisible to Asterisk
                      no matter what their presence says. */}
                  {!a.valid && (
                    <span className="ml-2 px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">
                      not usable
                    </span>
                  )}
                  {/* Louder than the status badge on purpose: this agent reads
                      as available everywhere else while every call sent to them
                      is lost. */}
                  {/* Louder than "rings, no answer", because this one is
                      invisible from the agent's side too: their console looks
                      normal and the queue simply never tries them. */}
                  {a.never_offered_a_call && (
                    <span
                      className="ml-2 px-2 py-0.5 rounded text-xs bg-red-100 text-red-800"
                      title="Signed in while the queue was connecting calls to others, but this seat was never rung. Their phone is probably not connected — ask them to sign out and back in."
                    >
                      never rung
                    </span>
                  )}
                  {a.ringing_but_never_answers && (
                    <span
                      className="ml-2 px-2 py-0.5 rounded text-xs bg-red-100 text-red-800"
                      title={`Rung ${a.ring_no_answer} times, answered none. Their phone is probably not really connected.`}
                    >
                      rings, no answer ({a.ring_no_answer})
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {a.calls_taken > 0 ? (
                    a.calls_taken
                  ) : (
                    <span className="text-gray-300">0</span>
                  )}
                </td>
                <td className="px-4 py-2 text-xs text-gray-500">
                  <span title={a.last_call_at ?? undefined}>
                    {a.status === "on a call" ? (
                      <span className="rounded bg-sky-100 px-2 py-0.5 text-sky-800">
                        On call
                      </span>
                    ) : a.status === "offline" ? (
                      <span className="text-gray-300">—</span>
                    ) : (
                      <span
                        className={
                          waitedMinutes(a.last_call_at) >= 5
                            ? "rounded bg-red-100 px-2 py-0.5 font-medium text-red-800"
                            : waitedMinutes(a.last_call_at) >= 3
                              ? "rounded bg-amber-100 px-2 py-0.5 text-amber-800"
                              : ""
                        }
                      >
                        {timeSince(a.last_call_at)}
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  {a.status !== "offline" && <SignOutButton agent={a} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** How long since a moment, as a supervisor would say it.
 *
 * The floor asked for "time since last call" because the old column showed a
 * CLOCK TIME and it read as nonsense: the API returned a naive timestamp, so
 * the browser parsed UTC as local and echoed the same digits back whatever
 * zone the viewer was in. An elapsed duration sidesteps the question entirely
 * — "4m ago" means the same thing in Bogota, Chicago and Los Angeles, which
 * matters when the reps and the supervisor are in three different zones.
 */
/** Order the floor the way a manager reads it: whoever has waited longest at
 *  the top, because that is the person to do something about.
 *
 *  A rep mid-call is not waiting — "minutes since last call" cannot tell a rep
 *  the queue has forgotten from one deep in a long conversation, and putting a
 *  rep on a 13-minute call at the top of a "needs attention" list is exactly
 *  backwards. They sort below everyone waiting, and above nobody, so a busy
 *  floor reads as busy.
 *
 *  Reps who have taken no call at all sort to the very top: an unknown wait is
 *  at least as urgent as a long one.
 */
function byLongestWait(a: QueueAgentLive, b: QueueAgentLive): number {
  const rank = (x: QueueAgentLive) => {
    if (x.status === "offline") return 3;      // signed out — not the floor's problem
    if (x.status === "on a call") return 2;    // busy, not waiting
    return 0;
  };
  const ra = rank(a);
  const rb = rank(b);
  if (ra !== rb) return ra - rb;

  const waited = (x: QueueAgentLive) =>
    x.last_call_at ? Date.now() - new Date(x.last_call_at).getTime() : Infinity;
  return waited(b) - waited(a);
}

/** Whole minutes waited, for the colour thresholds. Infinity when they have
 *  never taken a call, so "never" always reads as urgent. */
function waitedMinutes(iso: string | null | undefined): number {
  if (!iso) return Infinity;
  const ms = Date.now() - new Date(iso).getTime();
  return Number.isNaN(ms) ? Infinity : ms / 60000;
}

function timeSince(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 0) return "just now";      // small clock skew between box and browser
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function QueueStatusPage() {
  // Assignment is the one thing on this page that WRITES, so it is tucked
  // behind a toggle: the page is glanced at all day, the matrix is used when
  // someone needs moving.
  const [showAssign, setShowAssign] = useState(false);
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ["queues", "live"],
    queryFn: listQueuesLive,
    refetchInterval: REFRESH_MS,
  });

  // Lanes are a throughput trick, not something a floor manager supervises.
  const queues = mergeLanes(data ?? []);
  const totalReady = queues.reduce((n, q) => n + q.ready + q.on_call, 0);
  const unstaffed = queues.filter((q) => q.no_one_taking_calls).length;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">Queue Status</h1>
          <p className="text-sm text-gray-500">
            Who is signed in and able to take transferred calls right now.
          </p>
        </div>
        {dataUpdatedAt > 0 && (
          <div className="text-xs text-gray-400">
            Updated {new Date(dataUpdatedAt).toLocaleTimeString()} · refreshes
            every {REFRESH_MS / 1000}s
          </div>
        )}
      </div>

      {isLoading && <div className="text-sm text-gray-500">Loading…</div>}

      {error && (
        <div className="bg-red-50 text-red-800 rounded-lg px-4 py-3 text-sm">
          Could not load queue status.
        </div>
      )}

      {!isLoading && !error && queues.length === 0 && (
        <div className="bg-white rounded-lg shadow px-4 py-6 text-sm text-gray-500">
          No queues yet. Assign agents to a campaign's transfer queue and they
          will appear here.
        </div>
      )}

      {queues.length > 0 && (
        <div className="bg-white rounded-lg shadow px-4 py-3 flex gap-8 items-center">
          <Stat
            label="agents taking calls"
            value={totalReady}
            tone={totalReady > 0 ? "text-green-700" : "text-red-700"}
          />
          <Stat
            label="queues with no one"
            value={unstaffed}
            tone={unstaffed > 0 ? "text-red-700" : "text-gray-400"}
          />
          <Stat label="queues" value={queues.length} />
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAssign((v) => !v)}
          aria-expanded={showAssign}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
        >
          <div>
            <div className="font-medium">Assign agents to queues</div>
            <div className="text-xs text-gray-500">
              Tick or untick a queue per agent — moving someone between inbound and outbound is
              one click each way.
            </div>
          </div>
          <span className="text-sm text-brand">{showAssign ? "Hide" : "Show"}</span>
        </button>
        {showAssign && (
          <div className="px-4 pb-4 border-t border-gray-100 pt-3">
            <QueueAssignmentMatrix />
          </div>
        )}
      </div>

      <TransfersLiveCard />

      <div className="space-y-4">
        {queues.map((q) => (
          <QueueCard key={q.campaign_id} queue={q} />
        ))}
      </div>
    </div>
  );
}


/** Sign one seat out, from the row it belongs to.
 *
 * Confirms first, and names the person: the click ends someone's session, and
 * the rows shift as the board refreshes, so "are you sure" is cheap insurance
 * against hitting the neighbour of the seat you meant.
 *
 * Mid-call seats keep the button but warn harder -- a manager sometimes does
 * need to cut a seat that is stuck showing "On call" with nobody there.
 */
function SignOutButton({ agent }: { agent: QueueAgentLive }) {
  const qc = useQueryClient();
  const onCall = agent.status === "on a call";
  const [failed, setFailed] = useState<string | null>(null);
  const kick = useMutation({
    mutationFn: () => signOutSeat(agent.extension_id),
    onSuccess: () => {
      setFailed(null);
      void qc.invalidateQueries({ queryKey: ["queues-live"] });
    },
    // The server refuses (409) while the seat is bridged. Without this the
    // button warned it would drop the call, the manager confirmed, and then
    // nothing visible happened at all -- which reads as the button being
    // broken rather than the server protecting a live customer.
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (e as Error)?.message ??
        "Could not sign this seat out.";
      setFailed(msg);
    },
  });
  const who = agent.extension_title || agent.extension_name;
  return (
    <div className="flex flex-col items-end gap-1">
    <button
      type="button"
      disabled={kick.isPending}
      onClick={() => {
        const warn = onCall
          ? `${who} shows as ON A CALL. Signing them out will drop it.\n\nSign them out anyway?`
          : `Sign ${who} out of the queue?`;
        if (window.confirm(warn)) kick.mutate();
      }}
      className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
      title={`Sign ${who} out now. They can sign back in themselves.`}
    >
      {kick.isPending ? "…" : "Sign out"}
    </button>
    {failed && (
      <span className="max-w-[16rem] text-right text-xs text-red-700">
        {failed}
      </span>
    )}
    </div>
  );
}
